import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";
import { Loader2, Receipt, TrendingUp, TrendingDown, ArrowRight, AlertCircle } from "lucide-react";

// Stable palette for GST-applicable heads (ordered by typical magnitude). Falls back to zinc
// for heads not in this map.
const HEAD_COLOR = {
  "Rent":                  "#7c3aed",  // violet — biggest fixed cost
  "Finance Cost":          "#FB7185",  // rose — the numerator itself
  "OTA Commission":        "#0E7490",  // cyan — booking platform fees
  "F&B Expenses":          "#FFA500",  // orange — food and beverage
  "Repairs & Maintenance": "#10B981",  // emerald — R&M
  "Housekeeping Charges":  "#20B2AA",  // teal
  "Hostel Laundry":        "#0891b2",
  "Guest Laundry":         "#06b6d4",
  "ID & Experiences":      "#FFCC00",  // amber
  "Communication":         "#a855f7",
  "Software":              "#9370DB",
  "BD Photography":        "#FFCC00",
  "Horticulture":          "#10B981",
  "Local Conveyance":      "#A78BFA",
  "Local Event":           "#6366F1",
  "Pet Expense":           "#FB7185",
  "Freight & Transport":   "#94a3b8",
  "Legal & Professional":  "#475569",
  "Payment Collection":    "#64748b",
  "Room Amenities":        "#22d3ee",
};

const fmtINR = (v) => v == null || isNaN(v) ? "—" : "₹" + Math.round(v).toLocaleString("en-IN");
const fmtL = (v) => v == null || isNaN(v) ? "—" : "₹" + (Number(v) / 100000).toFixed(2) + "L";
const fmtPct = (v) => v == null || isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`;

const lifecycleClasses = (tone) => ({
  indigo:  "text-indigo-800 bg-indigo-100 border-indigo-300",
  amber:   "text-amber-800 bg-amber-100 border-amber-300",
  rose:    "text-rose-800 bg-rose-100 border-rose-300",
  emerald: "text-emerald-800 bg-emerald-100 border-emerald-300",
}[tone] || "text-zinc-700 bg-zinc-100 border-zinc-200");

const itcTone = (pct, summary) => {
  if (pct == null) return "text-zinc-700 bg-zinc-50 border-zinc-200";
  const hi = summary?.benchmark_high_pct ?? 18;
  const lo = summary?.benchmark_low_pct ?? 15;
  if (pct > hi + 3) return "text-rose-800 bg-rose-50 border-rose-200";
  if (pct >= lo && pct <= hi + 3) return "text-emerald-800 bg-emerald-50 border-emerald-200";
  if (pct >= lo - 5) return "text-amber-800 bg-amber-50 border-amber-200";
  return "text-zinc-700 bg-zinc-50 border-zinc-200";
};

export default function PropertyFinanceCostDialog({ open, onClose, target, monthLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fy, setFy] = useState("recent");

  useEffect(() => {
    if (!open || !target) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams();
        if (fy && fy !== "recent") params.set("fy", fy);
        else params.set("months", "12");
        if (target.locations.length === 1) params.set("location", target.locations[0]);
        else params.set("locations", target.locations.join(","));
        const { data } = await api.get(`/mis-analysis/finance-cost-detail?${params.toString()}`);
        if (!cancelled) setData(data);
      } catch (e) {
        logError("finance-cost-detail fetch failed", e);
        if (!cancelled) setData(null);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, target, fy]);

  useEffect(() => {
    if (open && target) setFy("recent");
  }, [open, target]);

  if (!open) return null;
  const isCluster = (target?.locations?.length || 0) > 1;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-[1280px] max-h-[90vh] overflow-y-auto"
        data-testid="property-finance-cost-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-black text-lg flex items-center gap-2 flex-wrap">
            {target?.name}
            {isCluster && (
              <span className="text-[10px] uppercase tracking-wider font-bold border border-indigo-300 bg-indigo-50 text-indigo-800 px-2 py-0.5 rounded">
                Cluster · {target.locations.length} props
              </span>
            )}
            <span className="text-[10px] uppercase tracking-wider font-bold border border-violet-200 bg-violet-50 text-violet-800 px-2 py-0.5 rounded">
              Finance Cost · {fy === "recent" ? "last 12 months" : fy}
            </span>
          </DialogTitle>
          {data?.available_fys?.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap pt-2" data-testid="fc-dialog-fy-filter">
              <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mr-1">Period</span>
              <FyChip active={fy === "recent"} label="Last 12 mo" onClick={() => setFy("recent")} testid="fc-dialog-fy-recent" />
              {data.available_fys.map((f) => (
                <FyChip key={f} active={fy === f} label={f} onClick={() => setFy(f)} testid={`fc-dialog-fy-${f.replace(/\W/g, "")}`} />
              ))}
            </div>
          )}
        </DialogHeader>

        {loading && (
          <div className="py-16 flex items-center justify-center text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> loading finance cost detail…
          </div>
        )}

        {!loading && data && data.rows?.length === 0 && (
          <div className="py-16 text-center text-zinc-500 text-[11px]">
            No Finance Cost data for the selected period.
          </div>
        )}

        {!loading && data && data.rows?.length > 0 && (
          <div className="space-y-4">
            <KPIStrip data={data} fy={fy} />
            {isCluster && data.locations?.length > 1 && (
              <PropertiesStrip locations={data.locations} summary={data.summary} />
            )}
            <BaseCompositionStrip data={data} />
            <FcVsBaseTrend data={data} />
            <MonthlyDetailTable data={data} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KPIStrip({ data, fy }) {
  const s = data.summary || {};
  const lat = s.latest || {};
  const periodLabel = data.fy_selected ? data.fy_selected : `last ${data.rows?.length || 12} months`;
  const pctTone = itcTone(s.pct_of_base, s);
  return (
    <section className="grid grid-cols-2 md:grid-cols-5 gap-2">
      <KPI label={`Finance Cost · ${periodLabel}`} value={fmtL(s.fc_total)} sub={fmtINR(s.fc_total)} accent="#7c3aed" />
      <KPI label={`GST-applicable base · ${periodLabel}`} value={fmtL(s.base_total)} sub={fmtINR(s.base_total)} accent="#10b981" />
      <div className={`bg-white border-2 rounded-md p-2.5 ${pctTone}`} data-testid="fc-dialog-kpi-pct">
        <div className="text-[9.5px] uppercase font-bold tracking-wider opacity-80">FC % of base · {periodLabel}</div>
        <div className="font-display font-black tabular-nums mt-1" style={{ fontSize: 17, lineHeight: 1.4, paddingBottom: 2 }}>
          {s.pct_of_base == null ? "—" : `${s.pct_of_base}%`}
        </div>
        <div className="text-[10px] tabular-nums opacity-80">vs {s.benchmark_low_pct}-{s.benchmark_high_pct}% target</div>
      </div>
      <KPI
        label={`Latest · ${lat.month_label || ""}`}
        value={lat.pct_of_base != null ? `${lat.pct_of_base}%` : "—"}
        sub={lat.finance_cost ? `${fmtL(lat.finance_cost)} / ${fmtL(lat.gst_base)}` : "—"}
        accent="#FB7185"
      />
      <KPI
        label="Top base contributor"
        value={data.top_heads?.[0]?.head || "—"}
        sub={data.top_heads?.[0] ? `${fmtL(data.top_heads[0].total)} · ${data.top_heads[0].pct_of_base}% of base` : "—"}
        accent="#FFA500"
      />
    </section>
  );
}

function KPI({ label, value, sub, accent }) {
  return (
    <div className="bg-zinc-50/60 border border-zinc-200 rounded-md p-2.5">
      <div className="text-[9.5px] uppercase font-bold tracking-wider text-zinc-500">{label}</div>
      <div className="font-display font-black tabular-nums mt-1 text-zinc-900" style={{ fontSize: 17, lineHeight: 1.4, paddingBottom: 2 }}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5 tabular-nums overflow-hidden text-ellipsis whitespace-nowrap">{sub}</div>}
      <div className="mt-1 h-[2px] w-5 rounded-full" style={{ background: accent }} />
    </div>
  );
}

function PropertiesStrip({ locations, summary }) {
  return (
    <section className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">Properties in this group · latest month</div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {locations.map((l) => {
          const pctTone = itcTone(l.latest_pct, summary);
          return (
            <div key={l.location} className="bg-white border border-zinc-200 rounded p-2">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="font-bold text-[11.5px] text-zinc-800">{l.location}</span>
                {l.lifecycle_status && l.lifecycle_status !== "operational" && (
                  <span className={`text-[8.5px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded ${lifecycleClasses(l.lifecycle_tone)}`}>
                    {l.lifecycle_label}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-zinc-500">{l.type} · {l.model}</div>
              <div className="mt-1 tabular-nums text-[11px] text-zinc-700 flex items-center gap-2">
                FC <span className="font-bold">{fmtL(l.latest_fc)}</span> ·
                <span className={`inline-block px-1 py-0.5 rounded border tabular-nums font-bold text-[10px] ${pctTone}`}>
                  {l.latest_pct != null ? `${l.latest_pct}%` : "—"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function BaseCompositionStrip({ data }) {
  const top = (data.top_heads || []).slice(0, 10);
  if (!top.length) return null;
  const baseTotal = data.summary?.base_total || 0;
  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="fc-dialog-base-composition">
      <header className="mb-3 flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 flex items-center gap-1.5">
            <Receipt className="w-3 h-3" /> GST-applicable expense base · composition
          </div>
          <h3 className="font-display font-black text-[15px]">Which heads pump the ITC denominator?</h3>
        </div>
        <div className="text-[10.5px] text-zinc-500 tabular-nums">total base {fmtL(baseTotal)}</div>
      </header>
      {/* Single stacked bar showing % share by head */}
      <div className="flex h-4 rounded overflow-hidden border border-zinc-200 bg-zinc-50 mb-3">
        {top.map((h) => (
          <div key={h.head}
               title={`${h.head}: ${fmtL(h.total)} · ${h.pct_of_base}%`}
               style={{ flexBasis: `${h.pct_of_base}%`, background: HEAD_COLOR[h.head] || "#94a3b8" }} />
        ))}
      </div>
      {/* Per-head cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {top.map((h) => {
          const isFc = h.head === "Finance Cost";
          return (
            <div key={h.head} className={`rounded-md p-2 border ${isFc ? "bg-rose-50 border-rose-200" : "bg-white border-zinc-200"}`} data-testid={`fc-dialog-head-${h.head.replace(/\W/g, "")}`}>
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: HEAD_COLOR[h.head] || "#94a3b8" }} />
                <span className={`font-bold text-[11px] ${isFc ? "text-rose-800" : "text-zinc-800"}`}>{h.head}</span>
                {isFc && <span className="text-[8.5px] uppercase tracking-wider font-bold bg-rose-100 text-rose-700 border border-rose-200 px-1 rounded">numerator</span>}
              </div>
              <div className="tabular-nums font-display font-black text-[14px] text-zinc-900" style={{ lineHeight: 1.4, paddingBottom: 2 }}>{fmtL(h.total)}</div>
              <div className="tabular-nums text-[10.5px] text-zinc-600">{h.pct_of_base}% of base</div>
              <div className="mt-1 h-1 rounded-full bg-zinc-100 overflow-hidden">
                <div className="h-full" style={{ width: `${h.pct_of_base}%`, background: HEAD_COLOR[h.head] || "#94a3b8" }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-[10px] text-zinc-500 italic flex items-start gap-1.5">
        <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
        <span>
          Salary, all 6 Utility components, Utilities rollup and Depreciation are <span className="font-bold">excluded</span> from this base because they don't carry GST.
          {data.summary?.latest?.exempt_heads && Object.keys(data.summary.latest.exempt_heads).length > 0 && (
            <> Latest-month exempt heads totaled {fmtL(Object.values(data.summary.latest.exempt_heads).reduce((a, b) => a + b, 0))}.</>
          )}
        </span>
      </div>
    </section>
  );
}

function FcVsBaseTrend({ data }) {
  if (!data.rows?.length) return null;
  const maxV = Math.max(1, ...data.rows.map((r) => r.gst_base || 0));
  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="fc-dialog-trend">
      <header className="mb-3">
        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Monthly trend · Finance Cost as a slice of GST base</div>
        <h3 className="font-display font-black text-[15px]">Each month: Base (zinc) with FC overlaid in violet</h3>
      </header>
      <div className="grid gap-1.5 items-end" style={{ gridTemplateColumns: `repeat(${data.rows.length}, minmax(0, 1fr))` }}>
        {data.rows.map((r, i) => {
          const baseH = Math.max(8, Math.round((r.gst_base || 0) / maxV * 80));
          const fcShare = r.gst_base ? r.finance_cost / r.gst_base : 0;
          const fcH = Math.round(baseH * fcShare);
          return (
            <div key={r.key} className="flex flex-col items-center" data-testid={`fc-dialog-trend-bar-${i}`}>
              <div className="text-[9px] tabular-nums leading-tight mb-1 text-zinc-500">{fmtL(r.gst_base)}</div>
              <div className="w-full rounded-t relative overflow-hidden" style={{ height: baseH, background: "#e4e4e7" }}
                   title={`${r.month_label} · FC ${fmtL(r.finance_cost)} of base ${fmtL(r.gst_base)} = ${r.pct_of_base}%`}>
                <div className="absolute bottom-0 left-0 right-0" style={{ height: fcH, background: "#7c3aed" }} />
              </div>
              <div className="text-[9px] text-violet-700 tabular-nums font-bold mt-0.5">{r.pct_of_base != null ? `${r.pct_of_base}%` : "—"}</div>
              <div className="text-[9px] text-zinc-500 mt-0.5">{r.month_label.replace(/\s\d{4}$/, "").slice(0, 3)}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-3 text-[10px] text-zinc-600">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "#7c3aed" }} />
          <span className="font-medium">Finance Cost</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm border border-zinc-300" style={{ background: "#e4e4e7" }} />
          <span className="font-medium">GST-applicable base (rest)</span>
        </div>
      </div>
    </section>
  );
}

function MonthlyDetailTable({ data }) {
  const rows = (data.rows || []).slice().reverse();
  // Top 5 contributing heads across the window (to render as columns)
  const topHeads = (data.top_heads || [])
    .filter((h) => h.head !== "Finance Cost")
    .slice(0, 5)
    .map((h) => h.head);
  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-4">
      <header className="mb-3">
        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Detailed history · per month</div>
        <h3 className="font-display font-black text-[15px]">FC + top base contributors month-by-month</h3>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-[11.5px] table-auto border-separate" style={{ borderSpacing: 0 }}>
          <thead>
            <tr className="text-left text-[9.5px] uppercase tracking-wider text-zinc-500">
              <th className="py-2 pr-2 sticky left-0 bg-white z-10 border-b border-zinc-200 min-w-[110px]">Month</th>
              <th className="py-2 px-1.5 text-right border-b border-zinc-200">FC</th>
              <th className="py-2 px-1.5 text-right border-b border-zinc-200">Base</th>
              <th className="py-2 px-1.5 text-right border-b border-zinc-200">FC % of base</th>
              {topHeads.map((h) => (
                <th key={h} className="py-2 px-1.5 text-right border-b border-zinc-200">
                  <div className="flex items-center justify-end gap-1">
                    <span className="inline-block w-2 h-2 rounded-sm" style={{ background: HEAD_COLOR[h] || "#94a3b8" }} />
                    {h.replace(" Expenses", "").replace(" Commission", "")}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const prev = rows[i + 1];
              const momPct = prev?.finance_cost ? Math.round((r.finance_cost - prev.finance_cost) / prev.finance_cost * 1000) / 10 : null;
              const t = momTone(momPct);
              const Icon = t.icon;
              return (
                <tr key={r.key} className="hover:bg-zinc-50" data-testid={`fc-dialog-row-${r.key}`}>
                  <td className="py-2 pr-2 border-b border-zinc-100 font-bold text-zinc-800 sticky left-0 bg-white">
                    <div className="flex items-center gap-2">
                      <span>{r.month_label}</span>
                      <span className="text-[9px] text-zinc-400 uppercase tracking-wider">{r.fy}</span>
                    </div>
                  </td>
                  <td className="py-2 px-1.5 text-right border-b border-zinc-100">
                    <div className="font-bold tabular-nums text-zinc-900">{fmtL(r.finance_cost)}</div>
                    {momPct != null && (
                      <div className={`inline-flex items-center gap-0.5 ${t.text} tabular-nums text-[9.5px] font-bold`}>
                        <Icon className="w-2.5 h-2.5" />
                        {momPct >= 0 ? "+" : ""}{momPct}%
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-1.5 text-right tabular-nums border-b border-zinc-100 text-zinc-700">{fmtL(r.gst_base)}</td>
                  <td className="py-2 px-1.5 text-right border-b border-zinc-100">
                    {r.pct_of_base != null ? (
                      <span className={`inline-block px-1.5 py-0.5 rounded border tabular-nums font-bold text-[10.5px] ${itcTone(r.pct_of_base, data.summary)}`}>
                        {r.pct_of_base}%
                      </span>
                    ) : <span className="text-[10.5px] text-zinc-400">—</span>}
                  </td>
                  {topHeads.map((h) => {
                    const v = (r.gst_heads || {})[h] || 0;
                    return (
                      <td key={h} className="py-2 px-1.5 text-right tabular-nums border-b border-zinc-100">
                        <div className={v ? "text-zinc-700" : "text-zinc-300"}>{v ? fmtL(v) : "—"}</div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function momTone(pct) {
  if (pct == null) return { text: "text-zinc-500", icon: ArrowRight };
  if (pct >= 25) return { text: "text-rose-700", icon: TrendingUp };
  if (pct >= 10) return { text: "text-amber-700", icon: TrendingUp };
  if (pct <= -10) return { text: "text-emerald-700", icon: TrendingDown };
  return { text: "text-zinc-600", icon: ArrowRight };
}

function FyChip({ active, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-2 py-0.5 rounded border text-[10.5px] uppercase tracking-wider font-bold transition ${
        active
          ? "bg-violet-600 text-white border-violet-600 shadow-sm"
          : "bg-white text-zinc-700 border-zinc-300 hover:border-violet-400 hover:text-violet-700"
      }`}
    >
      {label}
    </button>
  );
}
